"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { MobileBottomToolViewport } from "./MobileBottomToolViewport";
import { MobileBottomToolDock } from "./MobileBottomToolDock";
import { MobileBottomToolSheet } from "./MobileBottomToolSheet";
import { MOBILE_BOTTOM_TOOL_DOCK_HEIGHT } from "./constants";
import type {
  MobileBottomToolId,
  MobileBottomToolItem,
} from "./types";

interface MobileBottomToolContextValue {
  pageTitle?: string;
  tools: MobileBottomToolItem[];
  selectedToolId: MobileBottomToolId | null;
  openToolId: MobileBottomToolId | null;
  registerTool: (tool: MobileBottomToolItem) => () => void;
  updateTool: (tool: MobileBottomToolItem) => void;
  unregisterTool: (id: MobileBottomToolId) => void;
  setSelectedToolId: (id: MobileBottomToolId | null) => void;
  openSheet: (id: MobileBottomToolId) => void;
  closeSheet: () => void;
  toggleSheet: (id: MobileBottomToolId) => void;
}

const MobileBottomToolContext =
  React.createContext<MobileBottomToolContextValue | null>(null);

export function useMobileBottomTools(): MobileBottomToolContextValue {
  const context = React.useContext(MobileBottomToolContext);
  if (!context) {
    throw new Error(
      "useMobileBottomTools must be used within MobileBottomToolProvider",
    );
  }
  return context;
}

interface MobileBottomToolProviderProps {
  children: React.ReactNode;
  tools?: MobileBottomToolItem[];
  selectedToolId?: MobileBottomToolId | null;
  defaultSelectedToolId?: MobileBottomToolId | null;
  onSelectedToolChange?: (id: MobileBottomToolId | null) => void;
  defaultOpenToolId?: MobileBottomToolId | null;
  dockLabel?: string;
  pageTitle?: string;
  mobileOnly?: boolean;
  withContentInset?: boolean;
  dockClassName?: string;
  sheetClassName?: string;
}

function sortTools(tools: MobileBottomToolItem[]): MobileBottomToolItem[] {
  return tools
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function useControlledId(
  controlled: MobileBottomToolId | null | undefined,
  defaultValue: MobileBottomToolId | null | undefined,
  onChange?: (id: MobileBottomToolId | null) => void,
) {
  const [uncontrolled, setUncontrolled] =
    React.useState<MobileBottomToolId | null>(defaultValue ?? null);
  const value = controlled !== undefined ? controlled : uncontrolled;

  const setValue = React.useCallback(
    (next: MobileBottomToolId | null) => {
      if (controlled === undefined) setUncontrolled(next);
      onChange?.(next);
    },
    [controlled, onChange],
  );

  return [value, setValue] as const;
}

export function MobileBottomToolProvider({
  children,
  tools: controlledTools,
  selectedToolId: controlledSelectedToolId,
  defaultSelectedToolId = null,
  onSelectedToolChange,
  defaultOpenToolId = null,
  dockLabel,
  pageTitle,
  mobileOnly = true,
  withContentInset = true,
  dockClassName,
  sheetClassName,
}: MobileBottomToolProviderProps) {
  const [registeredTools, setRegisteredTools] = React.useState<
    Record<MobileBottomToolId, MobileBottomToolItem>
  >({});
  const [selectedToolId, setSelectedToolId] = useControlledId(
    controlledSelectedToolId,
    defaultSelectedToolId,
    onSelectedToolChange,
  );
  const [openToolId, setOpenToolId] =
    React.useState<MobileBottomToolId | null>(defaultOpenToolId);

  const registryTools = React.useMemo(
    () => sortTools(Object.values(registeredTools)),
    [registeredTools],
  );
  const controlledToolList = React.useMemo(
    () => (controlledTools ? sortTools(controlledTools) : null),
    [controlledTools],
  );
  const tools = controlledToolList ?? registryTools;

  const unregisterTool = React.useCallback((id: MobileBottomToolId) => {
    setRegisteredTools((prev) => {
      if (!prev[id]) return prev;
      const { [id]: _removed, ...next } = prev;
      return next;
    });
    setOpenToolId((current) => (current === id ? null : current));
  }, []);

  const updateTool = React.useCallback((tool: MobileBottomToolItem) => {
    setRegisteredTools((prev) => ({ ...prev, [tool.id]: tool }));
  }, []);

  const registerTool = React.useCallback(
    (tool: MobileBottomToolItem) => {
      updateTool(tool);
      return () => unregisterTool(tool.id);
    },
    [unregisterTool, updateTool],
  );

  const openSheet = React.useCallback((id: MobileBottomToolId) => {
    setOpenToolId(id);
  }, []);

  const closeSheet = React.useCallback(() => {
    setOpenToolId(null);
  }, []);

  const toggleSheet = React.useCallback((id: MobileBottomToolId) => {
    setOpenToolId((current) => (current === id ? null : id));
  }, []);

  const effectiveOpenToolId = React.useMemo(() => {
    if (!openToolId) return null;
    return tools.some(
      (tool) =>
        tool.id === openToolId &&
        (tool.kind === "panel" || tool.kind === "status-panel") &&
        tool.sheet,
    )
      ? openToolId
      : null;
  }, [openToolId, tools]);

  const selectedTool = React.useMemo(
    () => tools.find((tool) => tool.id === effectiveOpenToolId) ?? null,
    [effectiveOpenToolId, tools],
  );

  const handleToolPress = React.useCallback(
    async (tool: MobileBottomToolItem) => {
      if (tool.disabled || tool.loading || tool.pending) return;

      if (tool.kind === "status") return;

      if (tool.kind === "view") {
        setSelectedToolId(tool.id);
        tool.onSelect?.(tool.id);
        closeSheet();
        return;
      }

      if (tool.kind === "action") {
        await tool.onAction?.();
        tool.onSelect?.(tool.id);
        if (tool.closeOnAction ?? true) closeSheet();
        return;
      }

      if (tool.sheet) {
        tool.onSelect?.(tool.id);
        toggleSheet(tool.id);
      }
    },
    [closeSheet, setSelectedToolId, toggleSheet],
  );

  const context = React.useMemo<MobileBottomToolContextValue>(
    () => ({
      pageTitle,
      tools,
      selectedToolId,
      openToolId: effectiveOpenToolId,
      registerTool,
      updateTool,
      unregisterTool,
      setSelectedToolId,
      openSheet,
      closeSheet,
      toggleSheet,
    }),
    [
      closeSheet,
      effectiveOpenToolId,
      openSheet,
      pageTitle,
      registerTool,
      selectedToolId,
      setSelectedToolId,
      toggleSheet,
      tools,
      unregisterTool,
      updateTool,
    ],
  );

  return (
    <MobileBottomToolContext.Provider value={context}>
      {children}
      {withContentInset && tools.length > 0 && (
        <MobileBottomToolInset mobileOnly={mobileOnly} />
      )}
      {tools.length > 0 && (
        <MobileBottomToolViewport mobileOnly={mobileOnly}>
          <MobileBottomToolSheet
            key={
              selectedTool
                ? `${selectedTool.id}:${selectedTool.sheet?.defaultHeightRatio ?? "default"}`
                : "empty"
            }
            tool={selectedTool}
            mobileOnly={mobileOnly}
            className={cn("absolute pointer-events-auto", sheetClassName)}
            onClose={closeSheet}
          />
          <MobileBottomToolDock
            tools={tools}
            selectedToolId={selectedToolId}
            openToolId={effectiveOpenToolId}
            label={dockLabel}
            mobileOnly={mobileOnly}
            className={cn("absolute pointer-events-auto", dockClassName)}
            closeSheet={closeSheet}
            openSheet={openSheet}
            toggleSheet={toggleSheet}
            onToolPress={handleToolPress}
          />
        </MobileBottomToolViewport>
      )}
    </MobileBottomToolContext.Provider>
  );
}

interface MobileBottomToolRegistrationProps {
  tool: MobileBottomToolItem;
}

export function MobileBottomToolRegistration({
  tool,
}: MobileBottomToolRegistrationProps) {
  const { updateTool, unregisterTool } = useMobileBottomTools();

  React.useEffect(() => {
    return () => unregisterTool(tool.id);
  }, [tool.id, unregisterTool]);

  React.useEffect(() => {
    updateTool(tool);
  }, [tool, updateTool]);

  return null;
}

interface MobileBottomToolInsetProps {
  mobileOnly?: boolean;
  className?: string;
}

export function MobileBottomToolInset({
  mobileOnly = true,
  className,
}: MobileBottomToolInsetProps) {
  return (
    <div
      aria-hidden
      className={cn(
        "h-[calc(var(--mobile-bottom-tool-dock-height,72px)+16px+env(safe-area-inset-bottom))]",
        // 클래식 레이아웃 전 구간(<1024px, 셸 게이트와 일치)에서 본문 하단 여백 확보
        mobileOnly && "lg:hidden",
        className,
      )}
      style={{
        "--mobile-bottom-tool-dock-height": MOBILE_BOTTOM_TOOL_DOCK_HEIGHT,
      } as React.CSSProperties}
    />
  );
}
